/**
 * Wild Ice Botanicals — WI Tracker v3.0.0
 * ============================================================================
 * The custom behavioral tracking layer. Replaces the destroyed "Goldilocks
 * Tracker" v2.6 (see master-plan/meta-pixel/REFERENCE-wib-pixel-v2.6.txt) and
 * absorbs the inline Section_Reached fragments that lived in
 * home-below-fold.liquid / chrys-below-fold.liquid.
 *
 * SOURCE OF TRUTH: master-plan/meta-pixel/src/wi-tracker.js
 * Every revision is saved there BEFORE it touches Shopify. Never edit the
 * theme asset without updating the source file first.
 *
 * WHAT IT SENDS
 *   Meta (fbq trackCustom on the Shopify Meta app's pixel, top frame):
 *     WI_HighIntent    — once per session; first leg wins. Legs (signal=):
 *                        atc_intent | bundle_builder | quality_view | multi_pdp
 *     WI_EmailCaptured — once per session, on any email signup
 *   GA4 (gtag on Simprosys' top-frame tag):
 *     wib_high_intent      — once per LEG per session (richer than Meta side)
 *     wib_section_view     — first viewport entry per [data-track] element
 *     wib_accordion_opened — accordion/details opens
 *     generate_lead        — mirror of WI_EmailCaptured (Google recommended name)
 *   Meta match assist (owner-approved 2026-08-15, ON):
 *     on email signup only: fbq('set','userData',{em}) — raw lowercased email,
 *     hashed internally by Meta's pixel. Nothing else, ever.
 *
 * HARD RULES — DO NOT "IMPROVE" THESE AWAY (each one guards a verified failure):
 *   1. NEVER fbq('track', ...). Standard events (PageView/ViewContent/AddToCart/
 *      InitiateCheckout/Purchase) belong to the Shopify Meta app, browser+CAPI,
 *      deduped. A second source double-counts and corrupts delivery.
 *   2. NEVER fbq('init', ...). The app owns pixel 1463844890353208; a second
 *      init raises DUPLICATE_PIXEL_ID and can clobber the app's identity state.
 *   3. NEVER send external_id. Meta currently strips it for Shopify-integrated
 *      pixels, but only via a server-side gate that can flip; a random browser
 *      external_id would silently poison entity resolution account-wide
 *      (documented 2021 Shopify/Meta incident).
 *   4. NEVER fbq('setUserData', ...) — not a valid command (verified against
 *      the live fbevents.js bundle 2026-08-15; v2.6's calls were no-ops).
 *      The one sanctioned identity call is fbq('set','userData',{em}) (rule
 *      above), which this pixel accepts because AutomaticMatching +
 *      ShopifyAppIntegratedPixel opt-ins are both on.
 *   5. NEVER gtag('set','user_data', ...). Simprosys owns the Google identity
 *      path (enhanced conversions via Ads API); a second writer conflicts.
 *   6. NEVER hash anything here. Meta's pixel hashes em itself, after its own
 *      normalization. Shipping hash code = shipping bugs.
 *   7. No PII in any event parameter, either platform. Ever.
 *   8. A tracking failure must never break the storefront: every entry point
 *      is wrapped, listeners are passive where the spec allows, there are no
 *      blocking waits, and total absence of fbq/gtag degrades to silence.
 *
 * DEBUG
 *   ?wib_debug=true — console logging + window.WITracker inspector
 *   ?wib_dry=true   — everything runs and logs, nothing is sent to fbq/gtag
 *
 * CHANGELOG
 *   3.0.1 (2026-08-16) wib_section_view param renamed section_name -> section:
 *     matches the GA4 custom dimension the owner registered AND the param the
 *     legacy wib_section_reached events used (historical continuity).
 *   3.0.0 (2026-08) Full rebuild per master-plan/meta-pixel/DESIGN.md.
 *     - 20 event names -> 2 (Meta) + 4 (GA4); ~95% volume cut
 *     - all scroll/time/session_end telemetry retired (GA4 built-ins + Clarity
 *       cover it; session_end overcounted sessions 2.49x)
 *     - advanced-matching subsystem deleted (was dead code in v2.6) except the
 *       single sanctioned em call on signup
 *     - consent gate added (v2.6 had none); Section_Reached fragments absorbed
 *       as GA4-only wib_section_view; kill switch via theme setting
 * ============================================================================
 */
(function () {
  'use strict';

  var VERSION = '3.0.1';
  var Q = window.location.search;
  var DEBUG = /[?&]wib_debug=true/.test(Q);
  var DRY = /[?&]wib_dry=true/.test(Q);
  var PIXEL_TIMEOUT_MS = 6000;
  var QUALITY_DWELL_MS = DEBUG ? 15000 : 60000; // debug mode shortens the walk
  var QUALITY_MIN_INTERACTIONS = 2;
  var MULTI_PDP_COUNT = 3;
  var SS_KEY = 'wi3';

  function log() {
    if (DEBUG) {
      try { console.log.apply(console, ['[WI Tracker]'].concat([].slice.call(arguments))); } catch (e) {}
    }
  }

  // --------------------------------------------------------------------------
  // Session state (sessionStorage-backed; degrades to in-memory if unavailable)
  // --------------------------------------------------------------------------
  var mem = null;
  function loadState() {
    if (mem) return mem;
    try { mem = JSON.parse(window.sessionStorage.getItem(SS_KEY)) || {}; }
    catch (e) { mem = {}; }
    mem.hi = mem.hi || {};       // legs fired this session (atc_intent, bundle_builder, quality_view, multi_pdp)
    mem.pdps = mem.pdps || [];   // distinct product handles seen this session
    return mem;
  }
  function saveState() {
    try { window.sessionStorage.setItem(SS_KEY, JSON.stringify(mem)); } catch (e) {}
  }

  // --------------------------------------------------------------------------
  // Page + product context
  // --------------------------------------------------------------------------
  function getPageType() {
    var p = window.location.pathname;
    if (p === '/') return 'home';
    if (p.indexOf('/products/') !== -1) return 'product';
    if (p.indexOf('/collections/') !== -1) return 'collection';
    if (p.indexOf('/cart') === 0) return 'cart';
    if (p.indexOf('/blogs/') !== -1) {
      return /\/blogs\/[^/]+\/[^/]+/.test(p) ? 'article' : 'blog';
    }
    if (p.indexOf('/search') === 0) return 'search';
    if (p.indexOf('/apps/subscriptions') === 0) return 'box_builder';
    if (p.indexOf('/pages/') === 0) return 'page';
    if (p.indexOf('/account') !== -1) return 'account';
    return 'other';
  }

  function getProductHandle() {
    var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  function getProduct() {
    try {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      if (meta && meta.product) {
        var v = (meta.product.variants && meta.product.variants[0]) || {};
        return {
          id: String(meta.product.id),
          price: typeof v.price === 'number' ? v.price / 100 : null,
          name: v.name || null,
          handle: getProductHandle()
        };
      }
    } catch (e) {}
    var h = getProductHandle();
    return h ? { id: null, price: null, name: null, handle: h } : null;
  }

  // --------------------------------------------------------------------------
  // Consent (Shopify Customer Privacy). US store: absent API => allowed.
  // marketingAllowed gates fbq; analyticsProcessingAllowed gates gtag.
  // --------------------------------------------------------------------------
  var consent = { marketing: true, analytics: true, source: 'default-us' };

  function refreshConsent() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === 'function') {
        consent.marketing = cp.marketingAllowed() !== false;
        consent.analytics = typeof cp.analyticsProcessingAllowed === 'function'
          ? cp.analyticsProcessingAllowed() !== false
          : consent.marketing;
        consent.source = 'customerPrivacy';
      }
    } catch (e) {}
    log('consent', consent);
  }

  function initConsent() {
    refreshConsent();
    if (consent.source === 'default-us' && window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
      try {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (err) { if (!err) refreshConsent(); }
        );
      } catch (e) {}
    }
    document.addEventListener('visitorConsentCollected', refreshConsent);
  }

  // --------------------------------------------------------------------------
  // Destinations. We only ever CONSUME fbq/gtag created by the Shopify Meta
  // app and Simprosys (both OPEN-context web pixels that install into the top
  // frame). If either is absent after timeout, that side stays silent forever.
  // --------------------------------------------------------------------------
  var dest = { fbq: false, gtag: false, settled: false };
  var pending = []; // events queued until destinations settle

  function settleDestinations() {
    var start = Date.now();
    (function check() {
      var f = typeof window.fbq === 'function';
      var g = typeof window.gtag === 'function';
      if ((f && g) || Date.now() - start > PIXEL_TIMEOUT_MS) {
        dest.fbq = f;
        dest.gtag = g;
        dest.settled = true;
        log('destinations settled', dest);
        var q = pending; pending = [];
        for (var i = 0; i < q.length; i++) q[i]();
      } else {
        setTimeout(check, 200);
      }
    })();
  }

  function whenSettled(fn) {
    if (dest.settled) { fn(); } else { pending.push(fn); }
  }

  var history_ = []; // debug: last emissions

  function emitMeta(name, params) {
    whenSettled(function () {
      try {
        if (!consent.marketing) return log('meta suppressed (consent)', name);
        if (!dest.fbq) return log('meta unavailable', name);
        var eventID = 'wi3_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
        history_.push({ to: 'meta', name: name, params: params, eventID: eventID, dry: DRY, t: Date.now() });
        if (history_.length > 50) history_.shift();
        log('meta ->', name, params, eventID, DRY ? '(DRY)' : '');
        if (!DRY) window.fbq('trackCustom', name, params, { eventID: eventID });
      } catch (e) { log('emitMeta error', e); }
    });
  }

  function emitGa(name, params) {
    whenSettled(function () {
      try {
        if (!consent.analytics) return log('ga suppressed (consent)', name);
        if (!dest.gtag) return log('ga unavailable', name);
        var p = {};
        for (var k in params) { if (params[k] != null) p[k] = params[k]; }
        p.page_location = window.location.href;
        p.page_type = p.page_type || getPageType();
        history_.push({ to: 'ga4', name: name, params: p, dry: DRY, t: Date.now() });
        if (history_.length > 50) history_.shift();
        log('ga4 ->', name, p, DRY ? '(DRY)' : '');
        if (!DRY) window.gtag('event', name, p);
      } catch (e) { log('emitGa error', e); }
    });
  }

  // --------------------------------------------------------------------------
  // WI_HighIntent — the composite gate.
  // Meta: once per session (first leg). GA4: once per leg per session.
  // --------------------------------------------------------------------------
  function fireHighIntent(signal, extra) {
    var s = loadState();
    if (s.hi[signal]) return;
    s.hi[signal] = 1;

    var product = getProduct();
    var params = { signal: signal, page_path: window.location.pathname };
    if (product && product.id) {
      params.content_ids = [product.id];
      if (product.price != null) { params.value = product.price; params.currency = 'USD'; }
    }
    if (extra) { for (var k in extra) { if (extra[k] != null) params[k] = extra[k]; } }

    if (!s.hi._meta) {
      s.hi._meta = 1;
      emitMeta('WI_HighIntent', params);
    }
    emitGa('wib_high_intent', {
      signal: signal,
      product_handle: product ? product.handle : null
    });
    saveState();
  }

  // Leg a: add-to-cart interaction begins
  function isAtcTarget(el) {
    if (!el) return false;
    if (el.closest('form[action*="/cart/add"]')) return true;
    var btn = el.closest('button, [role="button"], input[type="submit"]');
    if (!btn) return false;
    if (btn.getAttribute('name') === 'add') return true;
    if (btn.hasAttribute('data-variant-id')) return true;
    var t = (btn.textContent || '').toLowerCase();
    return t.indexOf('add to cart') !== -1;
  }

  // Leg b: bundle-builder / subscription engagement
  function isBundleTarget(el) {
    if (!el) return false;
    var a = el.closest('a[href]');
    if (a) {
      var href = a.getAttribute('href') || '';
      if (href.indexOf('/apps/subscriptions') !== -1 || href.indexOf('wib_action=') !== -1) return true;
    }
    if (el.closest('[name="selling_plan"], .appstle_subscribe_option, [data-selling-plan]')) return true;
    return false;
  }

  // Leg c: quality view (PDP: >=60s visible dwell AND >=2 meaningful interactions)
  var dwell = { visibleMs: 0, since: null, interactions: 0, timer: null };

  function dwellTick() {
    if (dwell.since != null) {
      dwell.visibleMs += Date.now() - dwell.since;
      dwell.since = Date.now();
    }
    if (dwell.visibleMs >= QUALITY_DWELL_MS && dwell.interactions >= QUALITY_MIN_INTERACTIONS) {
      clearInterval(dwell.timer);
      fireHighIntent('quality_view', { dwell_s: Math.round(dwell.visibleMs / 1000) });
    }
  }

  function initQualityView() {
    if (getPageType() !== 'product') return;
    dwell.since = document.visibilityState === 'visible' ? Date.now() : null;
    document.addEventListener('visibilitychange', function () {
      try {
        if (document.visibilityState === 'visible') { dwell.since = Date.now(); }
        else if (dwell.since != null) { dwell.visibleMs += Date.now() - dwell.since; dwell.since = null; }
      } catch (e) {}
    });
    dwell.timer = setInterval(function () { try { dwellTick(); } catch (e) {} }, 5000);
  }

  function countsAsInteraction(el) {
    if (!el || el.closest('header, footer, nav')) return false;
    return !!el.closest('summary, [aria-expanded], button, select, input, .thumb, [data-media], a[href^="#"]');
  }

  // Leg d: 3+ distinct PDPs this session
  function trackMultiPdp() {
    var h = getProductHandle();
    if (!h) return;
    var s = loadState();
    if (s.pdps.indexOf(h) === -1) {
      s.pdps.push(h);
      saveState();
    }
    if (s.pdps.length >= MULTI_PDP_COUNT) fireHighIntent('multi_pdp', { pdp_count: s.pdps.length });
  }

  // --------------------------------------------------------------------------
  // Email capture -> WI_EmailCaptured / generate_lead / em match assist
  // --------------------------------------------------------------------------
  function classifyCaptureLocation(node) {
    try {
      if (node && node.closest && node.closest('.popup, [class*="popup"], [class*="modal"], [role="dialog"]')) return 'popup';
      if (node && node.closest && node.closest('footer')) return 'footer';
    } catch (e) {}
    return 'embedded';
  }

  function handleEmailCaptured(email, where) {
    var s = loadState();
    if (s.email) return;
    s.email = 1;
    saveState();
    emitMeta('WI_EmailCaptured', { capture_location: where, page_path: window.location.pathname });
    emitGa('generate_lead', { capture_location: where });

    // Owner-approved match assist (2026-08-15): the ONE identity call allowed.
    // Raw lowercase email; Meta's pixel normalizes + SHA-256 hashes internally.
    // This pixel accepts 'set userData' (AutomaticMatching +
    // ShopifyAppIntegratedPixel opt-ins verified on). Never any other field.
    try {
      if (email && consent.marketing && dest.fbq && !DRY) {
        window.fbq('set', 'userData', { em: String(email).trim().toLowerCase() });
        log('em match assist sent');
      }
    } catch (e) { log('match assist error', e); }
  }

  function initEmailCapture() {
    // Path 1: any form containing a filled email input
    document.addEventListener('submit', function (e) {
      try {
        var form = e.target;
        if (!form || !form.querySelector) return;
        var input = form.querySelector('input[type="email"]');
        if (input && input.value && input.value.indexOf('@') !== -1) {
          handleEmailCaptured(input.value, classifyCaptureLocation(form));
        }
      } catch (err) {}
    }, true);

    // Path 2: Klaviyo forms (AJAX; native submit may not bubble usefully)
    window.addEventListener('klaviyoForms', function (e) {
      try {
        var d = e && e.detail;
        if (!d || (d.type !== 'submit' && d.type !== 'submittedForm')) return;
        var email = (d.metaData && (d.metaData.$email || d.metaData.email)) || null;
        if (!email) {
          var inp = document.querySelector('.klaviyo-form input[type="email"], [data-testid="form-component"] input[type="email"]');
          if (inp && inp.value) email = inp.value;
        }
        handleEmailCaptured(email, 'popup');
      } catch (err) {}
    });
  }

  // --------------------------------------------------------------------------
  // Section views (absorbs the old inline Section_Reached fragments; GA4 only)
  // --------------------------------------------------------------------------
  var sectionObserver = null;
  var observedSections = typeof WeakSet === 'function' ? new WeakSet() : null;
  var pageLoadTime = Date.now();

  function observeSection(el) {
    if (!sectionObserver || !el) return;
    if (observedSections) {
      if (observedSections.has(el)) return;
      observedSections.add(el);
    }
    sectionObserver.observe(el);
  }

  function initSections() {
    if (!('IntersectionObserver' in window)) return;
    var seen = {};
    sectionObserver = new IntersectionObserver(function (entries) {
      try {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (!entry.isIntersecting) continue;
          var name = entry.target.getAttribute('data-track');
          sectionObserver.unobserve(entry.target);
          if (!name || seen[name]) continue;
          seen[name] = 1;
          emitGa('wib_section_view', {
            section: name,
            seconds_to_view: Math.round((Date.now() - pageLoadTime) / 1000),
            product_handle: getProductHandle()
          });
        }
      } catch (e) {}
    }, { threshold: 0.1 });

    var els = document.querySelectorAll('[data-track]');
    for (var i = 0; i < els.length; i++) observeSection(els[i]);

    // Section Rendering API can replace subtrees; re-scan lightly on DOM adds.
    if ('MutationObserver' in window) {
      var pendingScan = null;
      new MutationObserver(function () {
        if (pendingScan) return;
        pendingScan = setTimeout(function () {
          pendingScan = null;
          try {
            var els2 = document.querySelectorAll('[data-track]');
            for (var j = 0; j < els2.length; j++) observeSection(els2[j]);
          } catch (e) {}
        }, 500);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  // --------------------------------------------------------------------------
  // Accordion opens (GA4 only)
  // --------------------------------------------------------------------------
  function accordionName(el) {
    var t = '';
    try {
      var title = el.querySelector && el.querySelector('.accordion__title, .summary__title');
      t = (title ? title.textContent : el.textContent) || '';
    } catch (e) {}
    return t.replace(/\s+/g, ' ').trim().slice(0, 60) || 'unnamed';
  }

  function isOpening(el) {
    try {
      var details = el.closest('details');
      if (details) return !details.open; // click precedes toggle
      var exp = el.closest('[aria-expanded]');
      if (exp) return exp.getAttribute('aria-expanded') === 'false';
    } catch (e) {}
    return true;
  }

  // --------------------------------------------------------------------------
  // Global click handler (single delegated listener; passive + capture)
  // --------------------------------------------------------------------------
  function handleClick(e) {
    try {
      var el = e.target instanceof Element ? e.target : null;
      if (!el) return;

      if (countsAsInteraction(el)) dwell.interactions++;

      var acc = el.closest('summary, [aria-expanded], .accordion__trigger, .faq__question');
      // Navigation/cart drawers use <summary> too — those are menu opens, not
      // content engagement. Only content accordions count.
      if (acc && acc.closest('header, footer, nav, cart-drawer, .menu-drawer, .drawer, [id*="Drawer"], [id*="drawer"]')) acc = null;
      if (acc && isOpening(acc)) {
        emitGa('wib_accordion_opened', {
          accordion_name: accordionName(acc),
          product_handle: getProductHandle()
        });
      }

      if (isBundleTarget(el)) { fireHighIntent('bundle_builder'); return; }
      if (isAtcTarget(el)) { fireHighIntent('atc_intent'); }
    } catch (err) { log('click handler error', err); }
  }

  // --------------------------------------------------------------------------
  // Init
  // --------------------------------------------------------------------------
  function init() {
    log('WI Tracker v' + VERSION + ' init', { page: getPageType(), dry: DRY });
    initConsent();
    settleDestinations();

    document.addEventListener('click', handleClick, { capture: true, passive: true });
    initEmailCapture();
    initSections();
    initQualityView();
    trackMultiPdp();
    if (getPageType() === 'box_builder') fireHighIntent('bundle_builder');

    if (DEBUG) {
      window.WITracker = {
        version: VERSION,
        state: function () { return JSON.parse(JSON.stringify(loadState())); },
        consent: consent,
        destinations: dest,
        dwell: dwell,
        events: history_,
        product: getProduct,
        pageType: getPageType,
        fire: fireHighIntent // manual leg testing: WITracker.fire('atc_intent')
      };
      log('debug interface: window.WITracker');
    }
  }

  try {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
    } else {
      init();
    }
  } catch (e) {
    if (DEBUG) { try { console.error('[WI Tracker] fatal init error', e); } catch (e2) {} }
  }
})();
